/**
 * Καλάθι πρόχειρων αλλαγών — κολλημένο στο κάτω μέρος της οθόνης (§13.11).
 *
 * ΓΙΑΤΙ κολλημένο: τα πρόχειρα είναι ΜΟΝΙΜΑ (§13.6.1). Χωρίς σταθερή υπενθύμιση,
 * ο χειριστής φεύγει από την καρτέλα με 40 μισοτελειωμένες αλλαγές και τις βρίσκει
 * μια εβδομάδα μετά — πάνω σε τιμές που έχουν αλλάξει στο μεταξύ.
 */

import React from 'react';
import { AlertTriangle, FlaskConical, Loader2, ShoppingBasket, Trash2, XCircle } from 'lucide-react';
import { StoreSummary } from '../../services/platformCatalogApi';
import { storeShort } from './catalogUi';

interface Props {
  openEdits: number;
  conflicts: number;
  stores: StoreSummary[];
  canEdit: boolean;
  busy: boolean;
  /**
   * Φ5 (§13.19.Γ): πόσα κελιά είναι σημειωμένα «να μην υπάρχουν στο efood»,
   * ΣΤΙΣ ΓΡΑΜΜΕΣ ΠΟΥ ΕΙΝΑΙ ΦΟΡΤΩΜΕΝΕΣ.
   *
   * ΞΕΧΩΡΙΣΤΗ ΕΝΟΤΗΤΑ ΚΑΙ ΟΧΙ ΜΕΣΑ ΣΤΟ ΠΛΗΘΟΣ: μια διαγραφή δεν είναι «ακόμη
   * μία αλλαγή». Οι άλλες αλλάζουν έναν αριθμό και αναιρούνται με νέα αποστολή·
   * αυτή βγάζει το είδος από το μενού και το επαναφέρει μόνο νέα δημιουργία,
   * με ΑΛΛΟ κωδικό efood.
   */
  pendingDeletes?: number;
  /** Οι φορτωμένες γραμμές είναι λιγότερες από το σύνολο (σελιδοποίηση/φίλτρο). */
  deletesPartial?: boolean;
  onDryRun: () => void;
  onClearAll: () => void;
}

const DraftBasket: React.FC<Props> = ({
  openEdits, conflicts, stores, canEdit, busy, pendingDeletes = 0, deletesPartial = false,
  onDryRun, onClearAll,
}) => {
  if (openEdits <= 0) return null;
  const withEdits = stores.filter(s => s.open_edits > 0);

  return (
    <div className="sticky bottom-0 z-20 -mx-1 px-1 pb-1">
      <div className="bg-surface-2 border border-line-strong rounded-xl shadow-lg p-3 flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-2 text-sm text-t1">
          <ShoppingBasket className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <strong>{openEdits}</strong> πρόχειρες αλλαγές
          {withEdits.length > 0 && (
            <span className="text-t3">σε {withEdits.length} {withEdits.length === 1 ? 'κατάστημα' : 'καταστήματα'}</span>
          )}
        </span>

        <div className="flex flex-wrap gap-1 min-w-0">
          {withEdits.slice(0, 12).map(s => (
            <span
              key={s.store_id}
              title={`${s.store_name}: ${s.open_edits} αλλαγές`}
              className="px-1.5 py-0.5 rounded-lg text-[11px] bg-amber-500/15 text-amber-800 dark:text-amber-200"
            >
              {storeShort(s.store_name)} {s.open_edits}
            </span>
          ))}
          {withEdits.length > 12 && <span className="text-[11px] text-t3">+{withEdits.length - 12}</span>}
        </div>

        {conflicts > 0 && (
          <span className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400" title="Η τιμή άλλαξε στο efood μετά την αλλαγή σου — χρειάζεται νέα εισαγωγή">
            <AlertTriangle className="w-4 h-4" /> {conflicts} σε σύγκρουση
          </span>
        )}

        {/* Οι διαγραφές, ΧΩΡΙΣΤΑ και σε δικό τους χρώμα (§13.19.Γ). */}
        {pendingDeletes > 0 && (
          <span
            className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-red-300 dark:border-red-700/60 bg-red-500/10 text-red-700 dark:text-red-300"
            title={'Είδη σημειωμένα να ΜΗΝ υπάρχουν στο efood.\n'
              + 'Πριν τη μετάπτωση: δεν μπαίνουν στο payload.\n'
              + 'Μετά τη μετάπτωση: φεύγουν με DELETE /catalog.\n'
              + 'Αναιρούνται μόνο με «Καθαρισμός όλων» — και μόνο όσο δεν έχουν σταλεί.'
              + (deletesPartial ? '\n\nΜετρημένα στις γραμμές που είναι φορτωμένες τώρα.' : '')}
          >
            <XCircle className="w-4 h-4" /> {pendingDeletes} προς διαγραφή{deletesPartial ? ' (στις ορατές)' : ''}
          </span>
        )}

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={onClearAll}
            disabled={!canEdit || busy}
            className="px-3 py-2 bg-surface-2 hover:bg-surface-3 disabled:opacity-50 text-t1 border border-line-strong rounded-xl text-sm flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" /> Καθαρισμός όλων
          </button>
          <button
            type="button"
            onClick={onDryRun}
            disabled={!canEdit || busy}
            title="Δεν κάνει καμία εξερχόμενη κλήση — δείχνει τις ακριβείς κλήσεις που ΘΑ γίνονταν"
            className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium flex items-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
            Στεγνή δοκιμή
          </button>
        </div>
      </div>
    </div>
  );
};

export default DraftBasket;
